/**
 * La primera pantalla de la landing personalizada (diseño V6 del lienzo
 * «Landing Fundadores PT», «Versión 6 · Landing personalizada»). Sustituye al
 * héroe y al «O teu ponto de partida» de la V4; de «Quem aparece primeiro» para
 * abajo la landing no cambia.
 *
 *   - «Oferta reservada para [negocio]» y el titular con su nombre.
 *   - La tarjeta de SU negocio: la foto de portada de su ficha (no el icono),
 *     nombre, nota, reseñas, categoría, dirección y cuántos Tapstar tiene.
 *   - Las cuatro cifras ya cargadas. Con GMB salen solas; sin GMB se elige una
 *     búsqueda (las sugeridas o una suya) y el escaneo las rellena aquí mismo.
 *     Sin ficha a la que apunte su Tapstar, el buscador de siempre.
 *   - El precio con todo lo de la V4 (selector de locales, tachado por local,
 *     vagas que bajan) y «Começar o meu plano / para [negocio]». En ordenador,
 *     en la tarjeta de la derecha, fija al hacer scroll.
 *
 * Móvil y ordenador no llevan el mismo orden: en móvil el texto va después de
 * las cifras, en ordenador justo debajo del titular (`order-*`).
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, MapPin, Star, TrendingUp } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import * as API_REAL from '@/api/ofertaPtService';

import BotaoComecar from './BotaoComecar';
import DiagnosticoFlow from './DiagnosticoFlow';
import { cartoesDe } from './PontoDePartida';
import SeletorLocais, { usePrecoLocais } from './SeletorLocais';
import {
  COLUNA, CTA_LANDING, PrecoHero, Riscado, Vagas,
} from './Secoes';
import {
  DIAGNOSTICO, HERO, PONTO_DE_PARTIDA, PRECO, TUDO, V6,
} from './copy';

function nota(valor) {
  return valor == null ? null : String(valor).replace('.', ',');
}

/** La foto de su ficha; sin foto (o si no carga), la inicial del negocio. */
function Foto({ ficha }) {
  const [rota, setRota] = useState(false);
  useEffect(() => { setRota(false); }, [ficha.foto]);
  const clase = 'h-16 w-16 shrink-0 rounded-2xl md:h-[84px] md:w-[84px] md:rounded-[20px]';
  if (ficha.foto && !rota) {
    return (
      <img
        src={ficha.foto}
        alt={V6.fotoAlt(ficha.nome)}
        onError={() => setRota(true)}
        referrerPolicy="no-referrer"
        className={`${clase} bg-[#f5f5f7] object-cover`}
      />
    );
  }
  return (
    <span aria-hidden="true" className={`${clase} flex items-center justify-center bg-[#feae2c] text-[28px] font-extrabold text-[#1d1d1f] md:text-[34px]`}>
      {ficha.nome.trim().charAt(0).toUpperCase()}
    </span>
  );
}

function TarjetaNegocio({ ficha, dispositivos }) {
  const linea = [
    ficha.avaliacoes != null ? V6.avaliacoes(ficha.avaliacoes) : null,
    ficha.categoria || null,
  ].filter(Boolean);
  return (
    <div className="flex flex-col gap-3 rounded-[20px] border border-[#ececf0] bg-white p-4 shadow-[0_10px_28px_rgba(0,0,0,0.06)] md:p-5">
      <div className="flex items-center gap-3.5 md:gap-4">
        <Foto ficha={ficha} />
        <div className="flex min-w-0 flex-col gap-[3px]">
          <span className="text-[17px] font-extrabold tracking-[-0.3px] md:text-xl">{ficha.nome}</span>
          {(ficha.nota != null || linea.length > 0) && (
            <span className="text-[13px] leading-snug text-[#4a4a52] md:text-sm">
              {ficha.nota != null && (
                <>
                  <Star className="mr-1 inline h-[13px] w-[13px] fill-[#feae2c] align-[-2px] text-[#feae2c]" aria-hidden="true" />
                  <strong className="text-[#1d1d1f]">{nota(ficha.nota)}</strong>
                  {linea.length > 0 && ' · '}
                </>
              )}
              {linea.join(' · ')}
            </span>
          )}
          {ficha.morada && (
            <span className="flex items-center gap-1.5 text-[13px] text-[#6e6e76] md:text-sm">
              <MapPin className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
              <span className="truncate">{ficha.morada}</span>
            </span>
          )}
        </div>
      </div>
      {dispositivos > 0 && (
        <p className="m-0 flex items-center gap-2 border-t border-[#f0f0f3] pt-3 text-[13px] font-semibold text-[#1d6b34] md:text-sm">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#e8f6ec]">
            <Check className="h-3 w-3" aria-hidden="true" />
          </span>
          {V6.dispositivos(dispositivos)}
        </p>
      )}
    </div>
  );
}

/** Mientras Google responde la primera vez (POST `ficha`). */
function TarjetaCargando() {
  return (
    <div className="flex items-center gap-3.5 rounded-[20px] border border-[#ececf0] bg-white p-4" aria-busy="true">
      <span className="h-16 w-16 shrink-0 animate-pulse rounded-2xl bg-[#f0f0f3]" />
      <span className="flex flex-1 flex-col gap-2">
        <span className="h-4 w-2/3 animate-pulse rounded bg-[#f0f0f3]" />
        <span className="h-3 w-1/2 animate-pulse rounded bg-[#f0f0f3]" />
      </span>
    </div>
  );
}

function Cifras({ gmb, diagnostico }) {
  const pronto = diagnostico?.estado === 'pronto';
  return (
    <div className="grid grid-cols-2 gap-2.5 md:gap-3">
      {cartoesDe(gmb, diagnostico).map((c) => (
        <div key={c.texto} className="flex flex-col gap-1 rounded-2xl bg-[#f5f5f7] p-3.5 md:p-4">
          <span className={`text-[26px] font-extrabold tracking-[-0.8px] md:text-[30px] ${pronto ? 'text-[#1d1d1f]' : 'text-[#c7c7cc]'}`}>
            {c.valor}
          </span>
          <span className="text-[13px] leading-snug text-[#4a4a52]">{c.texto}</span>
        </div>
      ))}
    </div>
  );
}

function Potencial() {
  return (
    <p className="m-0 flex items-center gap-2 rounded-xl border border-[#b7e1c3] bg-[#f1faf3] px-3 py-2.5 text-sm font-bold text-[#1d6b34]">
      <TrendingUp className="h-4 w-4 shrink-0" aria-hidden="true" />
      {V6.potencial}
    </p>
  );
}

function codigoDe(e) {
  return e?.response?.data?.code || 'provedor';
}

/**
 * Sin GMB, con la ficha ya identificada: elegir la búsqueda y lanzar el
 * escaneo. Es el mismo diagnóstico que el buscador (preparar → lanzar →
 * sondear), sin los pasos de buscar y confirmar.
 */
function Pesquisas({ ficha, sugestoes, diagnostico, restantes, token, landingKey, api }) {
  const queryClient = useQueryClient();
  const [aCarregar, setACarregar] = useState(null);
  const [erro, setErro] = useState(diagnostico?.estado === 'erro' ? 'erro' : null);
  const [outra, setOutra] = useState('');
  const [aEscrever, setAEscrever] = useState(false);

  const lancar = async (palavra) => {
    const final = (palavra || '').trim();
    if (!final) {
      setErro('sem_categoria');
      return;
    }
    setErro(null);
    setACarregar(final);
    try {
      // Preparar guarda el negocio que se va a medir; lanzar solo acepta ese.
      await api.prepararDiagnostico(token, ficha.placeId);
      await api.lancarDiagnostico(token, { placeId: ficha.placeId, palavra: final });
      await queryClient.invalidateQueries({ queryKey: landingKey });
    } catch (e) {
      setErro(codigoDe(e));
    } finally {
      setACarregar(null);
    }
  };

  const analisando = diagnostico?.estado === 'a_analisar';
  const usada = diagnostico?.palavraChave || '';
  const bloqueado = analisando || !!aCarregar || restantes <= 0;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap gap-2" role="group" aria-label={V6.emQuePesquisa}>
        {sugestoes.map((p) => {
          const activa = usada === p || aCarregar === p;
          return (
            <CtaLink
              key={p}
              as="button"
              type="button"
              cta="landing.oferta-pt.analisar"
              onClick={() => lancar(p)}
              disabled={bloqueado}
              aria-pressed={activa}
              className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold disabled:cursor-not-allowed ${
                activa ? 'border-[#1d1d1f] bg-[#1d1d1f] text-white' : 'border-[#e5e5ea] bg-white text-[#1d1d1f] disabled:opacity-60'}`}
            >
              {aCarregar === p && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {p}
            </CtaLink>
          );
        })}
        <button
          type="button"
          onClick={() => setAEscrever(true)}
          disabled={bloqueado}
          aria-expanded={aEscrever}
          className="min-h-[44px] rounded-full border border-dashed border-[#c7c7cc] bg-white px-3.5 text-sm font-semibold text-[#4a4a52] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {DIAGNOSTICO.outraPalavra}
        </button>
      </div>
      {aEscrever && (
        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); lancar(outra); }}
        >
          <label className="flex-1">
            <span className="sr-only">{DIAGNOSTICO.outraPalavra}</span>
            <input
              type="text"
              value={outra}
              maxLength={60}
              autoFocus
              onChange={(e) => setOutra(e.target.value)}
              placeholder={DIAGNOSTICO.outraPlaceholder}
              className="min-h-[48px] w-full rounded-xl border-2 border-[#feae2c] px-3.5 text-base outline-none"
            />
          </label>
          <CtaLink
            as="button"
            type="submit"
            cta="landing.oferta-pt.analisar"
            disabled={bloqueado}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-[#feae2c] px-4 font-bold text-[#1d1d1f] disabled:cursor-wait disabled:opacity-70"
          >
            {aCarregar === outra.trim() && aCarregar && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {V6.ver}
          </CtaLink>
        </form>
      )}
      {restantes <= 0 && !analisando && (
        <p className="m-0 rounded-xl bg-[#fff4dd] px-3.5 py-2.5 text-sm font-semibold text-[#6b3d00]">{DIAGNOSTICO.semTentativas}</p>
      )}
      {erro && (
        <p role="alert" className="m-0 rounded-xl bg-[#fdecea] px-3.5 py-2.5 text-sm font-semibold text-[#8a1c12]">
          {DIAGNOSTICO.erros[erro] || DIAGNOSTICO.erros.provedor}
        </p>
      )}
    </div>
  );
}

/** El escaneo en directo, en la misma tarjeta de las cifras. */
function Analisando({ progresso }) {
  const total = progresso?.total || 49;
  const feitos = Math.min(progresso?.feitos || 0, total);
  const pct = Math.max(8, Math.round((feitos / total) * 100));
  return (
    <div className="flex flex-col gap-2" aria-live="polite">
      <span className="flex items-center gap-2 text-sm font-semibold">
        <Loader2 className="h-4 w-4 animate-spin text-[#feae2c]" aria-hidden="true" />
        {DIAGNOSTICO.aAnalisar}
      </span>
      <div className="h-2 overflow-hidden rounded-full bg-[#ececf0]">
        <div className="h-2 rounded-full bg-[#feae2c] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Las cuatro cifras con su título y lo que va debajo. Sin GMB, además, las
 * búsquedas para lanzar el escaneo.
 */
function DadosDaFicha({ gmb, ficha, sugestoes, diagnostico, escaneo, token, landingKey, api }) {
  const queryClient = useQueryClient();
  const d = diagnostico;
  const pronto = d?.estado === 'pronto';
  const analisando = !gmb && d?.estado === 'a_analisar';
  const [outraVez, setOutraVez] = useState(false);

  // Mientras se analiza, se pregunta el estado; al cambiar, se relee la landing
  // para que las cifras salgan con los números (como `DiagnosticoFlow`).
  const { data: estado } = useQuery({
    queryKey: [...landingKey, 'diagnostico'],
    queryFn: () => api.getDiagnostico(token),
    enabled: analisando,
    refetchInterval: 2500,
  });
  useEffect(() => {
    const e = estado?.diagnostico?.estado;
    if (e && e !== 'a_analisar') {
      setOutraVez(false);
      queryClient.invalidateQueries({ queryKey: landingKey });
    }
  }, [estado, landingKey, queryClient]);

  const podeEscolher = !gmb && escaneo?.disponivel && sugestoes !== null;
  const escolher = podeEscolher && (!pronto || outraVez);
  const titulo = gmb || pronto ? V6.fichaHoje(d?.palavraChave) : V6.emQuePesquisa;

  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-[15px] font-bold md:text-base">{titulo}</span>
      {escolher && (
        <Pesquisas
          ficha={ficha}
          sugestoes={sugestoes}
          diagnostico={d}
          restantes={escaneo.restantes}
          token={token}
          landingKey={landingKey}
          api={api}
        />
      )}
      <Cifras gmb={gmb} diagnostico={d} />
      {analisando && <Analisando progresso={estado?.progresso} />}
      {gmb && d?.estado === 'a_calcular' && (
        <p className="m-0 flex items-center gap-2 text-sm text-[#5e5e66]">
          <Loader2 className="h-4 w-4 animate-spin text-[#feae2c]" aria-hidden="true" />
          {PONTO_DE_PARTIDA.aCalcular}
        </p>
      )}
      {gmb && d?.estado === 'sem_mapa' && <p className="m-0 text-sm text-[#5e5e66]">{PONTO_DE_PARTIDA.semMapa}</p>}
      {(gmb || pronto) && <Potencial />}
      {gmb && <span className="text-xs text-[#6e6e76]">{V6.dadosHoje}</span>}
      {!gmb && pronto && <span className="text-xs leading-snug text-[#6e6e76]">{PONTO_DE_PARTIDA.notaScorePublico}</span>}
      {!gmb && !pronto && !analisando && escolher && (
        <span className="text-xs leading-snug text-[#6e6e76]">{V6.escolheUma}</span>
      )}
      {!gmb && pronto && podeEscolher && !outraVez && escaneo.restantes > 0 && (
        <button
          type="button"
          onClick={() => setOutraVez(true)}
          className="self-start text-sm font-semibold text-[#9a5700] underline underline-offset-2"
        >
          {V6.outraPesquisa}
        </button>
      )}
    </div>
  );
}

/** El precio del móvil, con el selector, las vagas y el botón. */
function PrecoMovel({ vagas, cierreAt, comecar, cargando, seletor, negocio }) {
  const preco = usePrecoLocais(seletor?.locais || 1);
  return (
    <div className="flex flex-col gap-3.5 rounded-[20px] border border-[#ececf0] bg-[#fafafa] p-4">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <Riscado valor={preco.riscado} className="text-[15px]" />
        <span className="text-[30px] font-extrabold tracking-[-1px]">{preco.total}</span>
        <span className="text-sm text-[#5e5e66]">
          {preco.varios ? `por ano · ${seletor.locais} locais` : V6.porAno}
        </span>
      </div>
      {seletor && <SeletorLocais {...seletor} />}
      <Vagas restantes={vagas.restantes} total={vagas.total} cierreAt={cierreAt} enCaja={false} />
      <BotaoComecar
        cta={CTA_LANDING.ctaHero}
        comecar={comecar}
        cargando={cargando}
        para={negocio || undefined}
        className="min-h-[60px] w-full"
      />
      <p className="m-0 text-center text-xs text-[#5e5e66]">
        {preco.varios ? HERO.letraPequena.replace(PRECO.preco, preco.total) : TUDO.garantiaTitulo}
      </p>
    </div>
  );
}

/**
 * @param {{ data: ReturnType<typeof import('@/api/ofertaPtService').mapLanding>,
 *           vagas: {restantes: number, total: number}, comecar: (id: string) => void,
 *           cargando: string|null, seletor: {locais: number, setLocais: Function},
 *           token?: string, landingKey: unknown[], api?: typeof API_REAL,
 *           fichaACarregar: boolean }} props
 */
export default function HeroPersonalizado({
  data, vagas, comecar, cargando, seletor, token, landingKey, api = API_REAL, fichaACarregar,
}) {
  const { oferta, diagnostico, escaneo, ficha } = data;
  const gmb = !!data.gmbConectado;
  const negocio = ficha?.nome || data.negocio || '';
  // Sin ficha (su Tapstar no apunta a ninguna, o Google no la dio), el buscador
  // de siempre, con la de su Tapstar propuesta si la hay.
  const conBuscador = !gmb && !ficha && !fichaACarregar && escaneo?.disponivel
    && (!diagnostico || ['a_analisar', 'erro'].includes(diagnostico.estado));
  const texto = gmb ? V6.textoGmb(diagnostico?.resenasSemResposta) : V6.textoSemGmb;

  return (
    <section className={`${COLUNA} grid gap-8 pb-10 pt-4 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] md:items-start md:gap-14 md:pb-20 md:pt-14`}>
      <div className="flex flex-col gap-[18px] md:gap-6">
        <div className="order-1 inline-flex items-center gap-2 self-start rounded-full bg-[#fff4dd] px-3 py-1.5 text-[13px] font-semibold text-[#6b3d00]">
          <span className="h-2 w-2 shrink-0 rounded-full bg-[#feae2c]" aria-hidden="true" />
          {negocio ? V6.reservada(negocio) : HERO.eyebrow}
        </div>
        <h1 className="order-2 m-0 text-[32px] font-extrabold leading-[1.1] tracking-[-1px] md:text-[52px] md:leading-[1.05] md:tracking-[-2px]">
          {V6.titulo(data.nombre, negocio)}
        </h1>
        <p className="order-5 m-0 text-base leading-relaxed text-[#4a4a52] md:order-3 md:text-xl">{texto}</p>

        <div className="order-3 flex flex-col gap-[18px] md:order-4">
          {ficha && <TarjetaNegocio ficha={ficha} dispositivos={data.dispositivos} />}
          {!ficha && fichaACarregar && <TarjetaCargando />}
          {conBuscador ? (
            <DiagnosticoFlow
              token={token}
              prefill={escaneo.prefill}
              restantes={escaneo.restantes}
              diagnostico={diagnostico}
              landingKey={landingKey}
              api={api}
            />
          ) : null}
        </div>

        {/* Con el buscador, el progreso lo enseña él: aquí solo las cifras. */}
        {conBuscador && (
          <div className="order-4 md:order-5">
            <Cifras gmb={false} diagnostico={diagnostico} />
          </div>
        )}
        {!conBuscador && (gmb || ficha || diagnostico) && (
          <div className="order-4 md:order-5">
            <DadosDaFicha
              gmb={gmb}
              ficha={ficha}
              sugestoes={ficha ? data.sugestoes : null}
              diagnostico={diagnostico}
              escaneo={escaneo}
              token={token}
              landingKey={landingKey}
              api={api}
            />
          </div>
        )}

        <div className="order-6 md:hidden">
          <PrecoMovel
            vagas={vagas} cierreAt={oferta.cierreAt} comecar={comecar} cargando={cargando}
            seletor={seletor} negocio={negocio}
          />
        </div>
      </div>
      <div className="hidden md:sticky md:top-24 md:block">
        <PrecoHero
          restantes={vagas.restantes} total={vagas.total} cierreAt={oferta.cierreAt}
          comecar={comecar} cargando={cargando} ctaComprar={CTA_LANDING.ctaHero} seletor={seletor}
          eyebrow={negocio ? V6.precoEyebrow(negocio) : HERO.eyebrow}
          para={negocio || undefined}
        />
      </div>
    </section>
  );
}
